import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarCheck, Plus, RefreshCw, Trash2, AlertCircle, Link2, Check, Ban,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui-bits";
import { toast } from "sonner";
import { fmtDate, fmtTime } from "@/lib/constants";

const PROVIDER_KEYS = ["google", "outlook", "apple", "calendly", "ical"];

export function ConnectCalendar({ onBusyChange }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ provider: "google", label: "", url: "" });
  const [busyId, setBusyId] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = () => api.get("/expert/calendar").then(({ data }) => setData(data)).catch(() => setData({ feeds: [], busy_count: 0 }));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.url.trim()) { toast.error(t("cal.toastPasteLink")); return; }
    setConnecting(true);
    try {
      const payload = { ...form, label: form.label || t(`cal.providers.${form.provider}`) };
      const { data } = await api.post("/expert/calendar/connect", payload);
      setData(data);
      setOpen(false);
      setForm({ provider: "google", label: "", url: "" });
      toast.success(t("cal.toastConnected", { count: data.busy_count }));
      onBusyChange?.();
    } catch (err) { toast.error(apiError(err, t("cal.toastConnectError"))); } finally { setConnecting(false); }
  };

  const sync = async (id) => {
    setBusyId(id);
    try { const { data } = await api.post(`/expert/calendar/${id}/sync`); setData(data); toast.success(t("cal.toastSynced")); onBusyChange?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setBusyId(id);
    try { const { data } = await api.delete(`/expert/calendar/${id}`); setData(data); toast.success(t("cal.toastRemoved")); onBusyChange?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusyId(null); }
  };

  const feeds = data?.feeds || [];

  return (
    <div className="card-milled rounded-[0.8rem] p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><Link2 size={17} strokeWidth={1.75} /></span>
          <div>
            <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{t("cal.connectedTitle")}</h3>
            <p className="text-sm text-muted-foreground">{t("cal.connectedDesc")}</p>
          </div>
        </div>
        <Button data-testid="connect-calendar-btn" onClick={() => setOpen(true)} variant="outline" className="rounded-lg gap-1.5 bg-card shrink-0"><Plus size={15} /> {t("cal.connectBtn")}</Button>
      </div>

      {data === null ? (
        <div className="py-6 grid place-items-center"><Spinner className="text-primary" /></div>
      ) : feeds.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-5 text-center">
          <p className="text-sm text-muted-foreground">{t("cal.noneConnected")}</p>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {data.busy_count > 0 && (
            <p className="text-xs text-warning inline-flex items-center gap-1.5 mb-1"><Ban size={13} /> {t("cal.busyBlocks", { count: data.busy_count })}</p>
          )}
          {feeds.map((f) => (
            <div key={f.feed_id} data-testid={`feed-${f.feed_id}`} className="flex items-center gap-3 rounded-xl border border-border p-3.5 transition-colors hover:border-primary/30 hover:bg-secondary/30">
              <span className={`grid place-items-center w-9 h-9 rounded-lg ${f.error ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>
                {f.error ? <AlertCircle size={16} /> : <CalendarCheck size={16} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{f.label} <span className="text-xs font-normal text-muted-foreground">· {PROVIDER_KEYS.includes(f.provider) ? t(`cal.providers.${f.provider}`) : f.provider}</span></p>
                {f.error ? (
                  <p className="text-xs text-destructive truncate">{f.error}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("cal.busyEvents", { count: f.event_count })}{f.last_synced ? t("cal.syncedAt", { date: fmtDate(f.last_synced), time: fmtTime(f.last_synced) }) : ""}</p>
                )}
              </div>
              <button onClick={() => sync(f.feed_id)} disabled={busyId === f.feed_id} data-testid={`sync-${f.feed_id}`} title={t("cal.toastSynced")} className="grid place-items-center w-8 h-8 rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary">
                {busyId === f.feed_id ? <Spinner /> : <RefreshCw size={15} />}
              </button>
              <button onClick={() => remove(f.feed_id)} disabled={busyId === f.feed_id} data-testid={`remove-${f.feed_id}`} title={t("common.remove")} className="grid place-items-center w-8 h-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-secondary">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-[0.7rem]">
          <DialogHeader><DialogTitle>{t("cal.dialogTitle")}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-1">{t("cal.dialogDesc")}</DialogDescription>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("cal.provider")}</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v, label: t(`cal.providers.${v}`) })}>
                <SelectTrigger data-testid="feed-provider" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{PROVIDER_KEYS.map((k) => <SelectItem key={k} value={k}>{t(`cal.providers.${k}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="rounded-lg bg-secondary/60 border border-border p-3 text-xs text-muted-foreground leading-relaxed">
              {t(`cal.help.${form.provider}`)}
            </div>
            <div className="space-y-1.5">
              <Label>{t("cal.label")}</Label>
              <Input data-testid="feed-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t(`cal.providers.${form.provider}`)} className="h-11 rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cal.url")}</Label>
              <Input data-testid="feed-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/basic.ics or webcal://…" className="h-11 rounded-lg" />
            </div>
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Check size={13} className="text-success" /> {t("cal.readonly")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button data-testid="feed-connect" onClick={connect} disabled={connecting} className="rounded-lg gap-1.5">{connecting ? <Spinner /> : <>{t("cal.connect")}</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
